/**
 * Лицензии, тарифы и подключение компаний (ТЗ 6.29–6.30).
 *
 * Портал — продукт для многих компаний: каждая работает в своём пространстве, а доступ ей даётся по
 * лицензии — подписка на портале помесячно или на год, установка на серверах заказчика по ключу или
 * пробный период. Тариф говорит, какие разделы включены и какие лимиты действуют; портал сам проверяет
 * лимиты и срок, предупреждает заранее и после льготного периода переводит компанию в «только чтение».
 */
import { z } from 'zod';
import { InviteMail, InviteMessage } from './invite-mail';
import { MODULES, type ModuleCode } from './modules';
import type { Access } from './schemas';

/* ───────────────────────── Разделы по тарифу ───────────────────────── */

/**
 * Разделы, которые есть у всех тарифов: без них портал не работает. Остальные разделы включаются
 * тарифом; раздел вне тарифа скрыт из меню, а его API отвечает отказом (ЛЦ-04).
 */
export const CORE_MODULES: ModuleCode[] = [
  'inbox',
  'calendar',
  'overview',
  'portfolio',
  'admin',
  'roadmap',
  'whatsnew',
  // База знаний нужна любому тарифу: регламенты и инструкции компании (поток аналитики; согласовать с потоком 1)
  'kb',
  // Отчёты и списки работают на праве analytics.view и правах источников; отдельного тарифа у них нет (поток аналитики; согласовать с потоком 1)
  'reports',
  // Списки — та же страница отчётов без конструктора: доступна любому тарифу
  'lists',
  'support',
];

/** Разделы, которые тариф может включать и выключать */
export const PLAN_MODULES: ModuleCode[] = MODULES.map((m) => m.code).filter((c) => !CORE_MODULES.includes(c));

/**
 * Какие права относятся к какому разделу: право «warehouse.*» живёт в разделе «Склад». Право, которое не
 * попало ни в один раздел тарифа (проекты, портфель, администрирование), относится к основе портала и
 * доступно всем тарифам.
 */
export const MODULE_PERMISSION_PREFIXES: Partial<Record<ModuleCode, string[]>> = {
  analytics: ['analytics.'],
  suppliers: ['suppliers.'],
  ai: ['ai.'],
  psd: ['psd.'],
  estimates: ['estimate.'],
  norms: ['norms.'],
  budget: ['budget.'],
  schedule: ['schedule.'],
  resources: ['resources.', 'timesheet.'],
  procurement: ['procurement.'],
  contracts: ['contracts.'],
  site: ['site.'],
  supervision: ['supervision.'],
  changes: ['changes.'],
  warehouse: ['warehouse.'],
  finance: ['finance.'],
  qhse: ['qhse.'],
  documents: ['documents.'],
  video: ['video.'],
};

/** Раздел, к которому относится право, или null для основы портала */
export function moduleOfPermission(permission: string): ModuleCode | null {
  for (const [code, prefixes] of Object.entries(MODULE_PERMISSION_PREFIXES)) {
    if (prefixes?.some((p) => permission.startsWith(p))) return code as ModuleCode;
  }
  return null;
}

/** Разрешает ли набор разделов тарифа это право; null — тариф без ограничений по разделам */
export function planAllowsPermission(
  planModules: readonly string[] | null | undefined,
  permission: string,
): boolean {
  if (!planModules) return true;
  const mod = moduleOfPermission(permission);
  return mod === null || planModules.includes(mod);
}

/** Виден ли раздел при этом тарифе */
export function planAllowsModule(
  planModules: readonly string[] | null | undefined,
  code: ModuleCode,
): boolean {
  if (!planModules) return true;
  return CORE_MODULES.includes(code) || planModules.includes(code);
}

/**
 * Права человека с учётом тарифа: права разделов, которых нет в тарифе, убираются из всех областей.
 * Так раздел вне тарифа сам пропадает из меню, а его API отвечает отказом, как и любое другое право (ЛЦ-04).
 */
export function restrictAccessToPlan(
  access: Access,
  planModules: readonly string[] | null | undefined,
): Access {
  if (!planModules) return access;
  const keep = (list: string[]) => list.filter((p) => planAllowsPermission(planModules, p));
  const scoped = (m: Record<string, string[]>) =>
    Object.fromEntries(Object.entries(m).map(([k, v]) => [k, keep(v)]));
  return {
    tenant: keep(access.tenant),
    ...(access.externalProjectIds ? { externalProjectIds: access.externalProjectIds } : {}),
    legalEntities: scoped(access.legalEntities),
    directions: scoped(access.directions),
    projects: scoped(access.projects),
    money: access.money,
    // Уровни денег по проектам (ДС-18) тариф не ограничивает
    ...(access.moneyIn ? { moneyIn: access.moneyIn } : {}),
  };
}

/** Права, которых у демо-компании нет: статус разработки посетителям демо не показывается */
export const DEMO_HIDDEN_PERMISSIONS: readonly string[] = ['roadmap.view'];

/** Права демо-компании: без служебных разделов о ходе разработки (меню и API) */
export function restrictAccessForDemo(access: Access): Access {
  const keep = (list: string[]) => list.filter((p) => !DEMO_HIDDEN_PERMISSIONS.includes(p));
  const scoped = (m: Record<string, string[]>) =>
    Object.fromEntries(Object.entries(m).map(([k, v]) => [k, keep(v)]));
  return {
    tenant: keep(access.tenant),
    ...(access.externalProjectIds ? { externalProjectIds: access.externalProjectIds } : {}),
    legalEntities: scoped(access.legalEntities),
    directions: scoped(access.directions),
    projects: scoped(access.projects),
    money: access.money,
    // Уровни денег по проектам (ДС-18) тариф не ограничивает
    ...(access.moneyIn ? { moneyIn: access.moneyIn } : {}),
  };
}

/* ───────────────────────── Лимиты ───────────────────────── */

/** Что ограничивает тариф: null — без ограничения */
export const LIMIT_KINDS = ['users', 'projects', 'files_mb', 'cameras', 'machines'] as const;
export type LimitKind = (typeof LIMIT_KINDS)[number];

export const LIMIT_TITLES: Record<LimitKind, string> = {
  users: 'Сотрудники',
  projects: 'Проекты',
  files_mb: 'Файлы, МБ',
  cameras: 'Камеры',
  machines: 'Единицы техники',
};

export const PlanLimits = z.object({
  users: z.number().int().positive().nullable(),
  projects: z.number().int().positive().nullable(),
  files_mb: z.number().int().positive().nullable(),
  cameras: z.number().int().nonnegative().nullable(),
  machines: z.number().int().nonnegative().nullable(),
});
export type PlanLimits = z.infer<typeof PlanLimits>;

export const NO_LIMITS: PlanLimits = {
  users: null,
  projects: null,
  files_mb: null,
  cameras: null,
  machines: null,
};

/** Лимиты компании: индивидуальные значения поверх тарифа (ЛЦ-01) */
export function effectiveLimits(
  plan: PlanLimits,
  override: Partial<PlanLimits> | null | undefined,
): PlanLimits {
  const out: PlanLimits = { ...plan };
  if (override) {
    for (const k of LIMIT_KINDS) {
      if (override[k] !== undefined) out[k] = override[k] ?? null;
    }
  }
  return out;
}

/* ───────────────────────── Тарифы ───────────────────────── */

export const Plan = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** Разделы сверх основы портала */
  modules: z.array(z.string()),
  limits: PlanLimits,
  /** Цены без НДС; null — по договорённости */
  priceMonth: z.number().nullable(),
  priceYear: z.number().nullable(),
  currency: z.string(),
  isActive: z.boolean(),
  /** Показывать на главной странице среди тарифов */
  isPublic: z.boolean(),
  sortOrder: z.number().int(),
  /** Сколько компаний на этом тарифе (действующие лицензии) */
  tenantsCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Plan = z.infer<typeof Plan>;

export const PlanInput = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9-]{1,30}$/, 'Код — латиница, цифры и дефис'),
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(500).nullable().optional(),
  /** Разделы сверх основы — только известные коды модулей, иначе опечатка молча выключит раздел */
  modules: z.array(z.enum(PLAN_MODULES as [ModuleCode, ...ModuleCode[]])).max(50),
  limits: PlanLimits,
  priceMonth: z.number().nonnegative().nullable().optional(),
  priceYear: z.number().nonnegative().nullable().optional(),
  currency: z.string().trim().length(3).default('KZT'),
  isActive: z.boolean().default(true),
  isPublic: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
});
export type PlanInput = z.infer<typeof PlanInput>;

/** Код тарифа после создания не меняется: по нему тариф находят сид и журнал */
export const PlanPatch = PlanInput.omit({ code: true }).partial();
export type PlanPatch = z.infer<typeof PlanPatch>;

/* ───────────────────────── Лицензии ───────────────────────── */

export const LICENSE_KINDS = ['saas', 'onprem', 'trial'] as const;
export type LicenseKind = (typeof LICENSE_KINDS)[number];
export const LICENSE_KIND_TITLES: Record<LicenseKind, string> = {
  saas: 'Подписка на портале',
  onprem: 'Установка у заказчика',
  trial: 'Пробный период',
};

/**
 * Состояние лицензии, которое вычисляет портал (ЛЦ-03):
 * active — действует; expiring — заканчивается в ближайшие 30 дней; grace — срок вышел, идёт льготный период
 * 14 дней; readonly — льготный период вышел, менять нельзя; suspended — приостановлена владельцем портала,
 * вход закрыт; none — лицензии нет (как readonly).
 */
export const LICENSE_STATES = ['active', 'expiring', 'grace', 'readonly', 'suspended', 'none'] as const;
export type LicenseState = (typeof LICENSE_STATES)[number];
export const LICENSE_STATE_TITLES: Record<LicenseState, string> = {
  active: 'Действует',
  expiring: 'Скоро заканчивается',
  grace: 'Льготный период',
  readonly: 'Только чтение',
  suspended: 'Приостановлена',
  none: 'Нет лицензии',
};

/** Льготный период после окончания срока, дней */
export const GRACE_DAYS = 14;
/** За сколько дней предупреждать об окончании */
export const EXPIRY_WARNINGS = [30, 14, 3] as const;
/** Пробный период для новой компании, дней */
export const TRIAL_DAYS = 14;

export const LimitUsage = z.object({
  kind: z.enum(LIMIT_KINDS),
  title: z.string(),
  used: z.number().int(),
  limit: z.number().int().nullable(),
});
export type LimitUsage = z.infer<typeof LimitUsage>;

export const License = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  planId: z.uuid(),
  planCode: z.string(),
  planName: z.string(),
  kind: z.enum(LICENSE_KINDS),
  /** Приостановлена владельцем портала */
  suspended: z.boolean(),
  suspendedReason: z.string().nullable(),
  startsAt: z.string(),
  /** null — бессрочная */
  endsAt: z.string().nullable(),
  seats: z.number().int().nullable(),
  autoRenew: z.boolean(),
  limitsOverride: PlanLimits.partial().nullable(),
  note: z.string().nullable(),
  /** Действующая лицензия компании (одна) или прежняя из истории */
  isCurrent: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type License = z.infer<typeof License>;

const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата — ГГГГ-ММ-ДД');

export const LicenseInput = z.object({
  planId: z.uuid(),
  kind: z.enum(LICENSE_KINDS),
  startsAt: DateOnly,
  endsAt: DateOnly.nullable(),
  seats: z.number().int().positive().nullable().optional(),
  autoRenew: z.boolean().default(false),
  limitsOverride: PlanLimits.partial().nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type LicenseInput = z.infer<typeof LicenseInput>;

export const SuspendInput = z.object({ reason: z.string().trim().min(3).max(500) });
export type SuspendInput = z.infer<typeof SuspendInput>;

/** Что видит компания о своей лицензии и что уходит в /me */
export const LicenseStatus = z.object({
  state: z.enum(LICENSE_STATES),
  kind: z.enum(LICENSE_KINDS).nullable(),
  planCode: z.string().nullable(),
  planName: z.string().nullable(),
  /** Разделы тарифа сверх основы; null — все разделы */
  modules: z.array(z.string()).nullable(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  /** Дней до окончания (отрицательное — просрочено); null — бессрочная */
  daysLeft: z.number().int().nullable(),
  /** Менять данные нельзя (readonly, suspended, none) */
  readOnly: z.boolean(),
  seats: z.number().int().nullable(),
  /** Продлевает владелец портала сам: компании предупреждение об окончании не показывается */
  autoRenew: z.boolean(),
  /** Причина приостановки — видна сотрудникам компании на экране приостановки */
  suspendedReason: z.string().nullable(),
  /** Использование лимитов — только на странице «Лицензия» и у владельца портала; в /me пусто */
  usage: z.array(LimitUsage),
});
export type LicenseStatus = z.infer<typeof LicenseStatus>;

/** Журнал лицензий и действий владельца портала (ЛЦ-02, ЛЦ-05) */
export const PlatformEvent = z.object({
  id: z.uuid(),
  tenantId: z.uuid().nullable(),
  tenantName: z.string().nullable(),
  actorId: z.uuid().nullable(),
  actorName: z.string().nullable(),
  action: z.string(),
  title: z.string(),
  details: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});
export type PlatformEvent = z.infer<typeof PlatformEvent>;

/** Страница «Лицензия» в администрировании компании (ЛЦ-03, ЛЦ-04) */
export const CompanyLicense = z.object({
  status: LicenseStatus,
  license: License.nullable(),
  history: z.array(License),
  events: z.array(PlatformEvent),
  /** Куда писать, чтобы расширить тариф или продлить лицензию */
  contact: z.string().nullable(),
  can: z.object({ view: z.boolean() }),
});
export type CompanyLicense = z.infer<typeof CompanyLicense>;

/* ───────────────────────── Кабинет владельца портала ───────────────────────── */

export const TenantSummary = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  status: z.string(),
  isDemo: z.boolean(),
  createdAt: z.string(),
  members: z.number().int(),
  projects: z.number().int(),
  license: License.nullable(),
  licenseState: z.enum(LICENSE_STATES),
  daysLeft: z.number().int().nullable(),
});
export type TenantSummary = z.infer<typeof TenantSummary>;

export const TenantDetail = z.object({
  tenant: TenantSummary,
  status: LicenseStatus,
  history: z.array(License),
  events: z.array(PlatformEvent),
  /** Администраторы компании: кому писать (без телефонов и паролей) */
  admins: z.array(z.object({ fullName: z.string(), email: z.string(), isOwner: z.boolean() })),
  /** Открытое приглашение первого администратора — ждёт принятия */
  pendingAdminInvite: z.object({ email: z.string(), expiresAt: z.string() }).nullable(),
});
export type TenantDetail = z.infer<typeof TenantDetail>;

export const TenantCreateInput = z.object({
  name: z.string().trim().min(2).max(200),
  slug: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9-]{1,40}$/, 'Код компании — латиница, цифры и дефис'),
  /** Администратор компании: получит приглашение владельцем */
  adminEmail: z.email().transform((s) => s.toLowerCase()),
  adminName: z.string().trim().min(2).max(200),
  planId: z.uuid(),
  /** Пробный период, дней; 0 — сразу лицензия на год по тарифу */
  trialDays: z.number().int().min(0).max(90).default(TRIAL_DAYS),
  /** Заявка, из которой создаётся компания */
  requestId: z.uuid().nullable().optional(),
  isDemo: z.boolean().default(false),
  /** Страна компании (МР-01); без указания — страна заявки или Казахстан */
  countryCode: z.string().trim().length(2).toUpperCase().optional(),
  /** Партнёр, привёдший компанию (МР-16) */
  partnerId: z.uuid().nullable().optional(),
});
export type TenantCreateInput = z.infer<typeof TenantCreateInput>;

export const TenantCreated = z.object({
  tenant: TenantSummary,
  /** Ссылка приглашения администратора — показывается один раз */
  inviteLink: z.string(),
  /** Письмо администратору с инструкцией «С чего начать» (БЗ-31) */
  mail: InviteMail.optional(),
  message: InviteMessage.optional(),
});
export type TenantCreated = z.infer<typeof TenantCreated>;

/** Новое приглашение администратора компании от владельца портала (ссылка потеряна или истекла) */
export const OwnerInviteInput = z.object({
  email: z.email().transform((s) => s.toLowerCase()),
  fullName: z.string().trim().min(2).max(200),
});
export type OwnerInviteInput = z.infer<typeof OwnerInviteInput>;
export const OwnerInviteResult = z.object({
  inviteLink: z.string(),
  email: z.string(),
  expiresAt: z.string(),
  mail: InviteMail.optional(),
  message: InviteMessage.optional(),
});
export type OwnerInviteResult = z.infer<typeof OwnerInviteResult>;

export const TenantPatch = z.object({
  name: z.string().trim().min(2).max(200).optional(),
  isDemo: z.boolean().optional(),
});
export type TenantPatch = z.infer<typeof TenantPatch>;

export const PlatformOverview = z.object({
  tenants: z.number().int(),
  active: z.number().int(),
  expiring: z.number().int(),
  readonly: z.number().int(),
  suspended: z.number().int(),
  newRequests: z.number().int(),
  plans: z.number().int(),
});
export type PlatformOverview = z.infer<typeof PlatformOverview>;

/* ───────────────────────── Заявка на доступ (ГЛ-04) ───────────────────────── */

export const ACCESS_REQUEST_STATUSES = ['new', 'contacted', 'created', 'rejected'] as const;
export type AccessRequestStatus = (typeof ACCESS_REQUEST_STATUSES)[number];
export const ACCESS_REQUEST_STATUS_TITLES: Record<AccessRequestStatus, string> = {
  new: 'Новая',
  contacted: 'Связались',
  created: 'Компания создана',
  rejected: 'Отклонена',
};

export const AccessRequestInput = z.object({
  company: z.string().trim().min(2).max(200),
  name: z.string().trim().min(2).max(200),
  phone: z.string().trim().min(5).max(40),
  email: z.email().transform((s) => s.toLowerCase()),
  interest: z.string().trim().max(2000).optional().default(''),
  /** Поле-ловушка для роботов: людям не видно; непустое — заявка «принимается», но не сохраняется */
  website: z.string().max(500).optional(),
  /** Страна, язык и метки ссылки (канал, кампания, партнёр) — для воронки по странам (МР-19) */
  countryCode: z.string().trim().length(2).toUpperCase().optional(),
  lang: z.string().trim().min(2).max(5).optional(),
  channel: z.string().trim().max(60).optional(),
  campaign: z.string().trim().max(80).optional(),
  partner: z.string().trim().max(40).optional(),
});
export type AccessRequestInput = z.infer<typeof AccessRequestInput>;

export const AccessRequest = z.object({
  id: z.uuid(),
  company: z.string(),
  name: z.string(),
  phone: z.string(),
  email: z.string(),
  interest: z.string(),
  status: z.enum(ACCESS_REQUEST_STATUSES),
  tenantId: z.uuid().nullable(),
  note: z.string().nullable(),
  countryCode: z.string().nullable().default(null),
  channel: z.string().nullable().default(null),
  campaign: z.string().nullable().default(null),
  partnerCode: z.string().nullable().default(null),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AccessRequest = z.infer<typeof AccessRequest>;

export const AccessRequestUpdate = z.object({
  status: z.enum(ACCESS_REQUEST_STATUSES),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type AccessRequestUpdate = z.infer<typeof AccessRequestUpdate>;

/** Тарифы для главной страницы: без внутренних полей */
export const PublicPlan = Plan.pick({
  code: true,
  name: true,
  description: true,
  modules: true,
  limits: true,
  priceMonth: true,
  priceYear: true,
  currency: true,
});
export type PublicPlan = z.infer<typeof PublicPlan>;

/** Открытые сведения для главной страницы (ГЛ-01): контакт владельца портала и включено ли демо */
export const SiteInfo = z.object({
  /** Почта или телефон владельца портала (PLATFORM_CONTACT); null — не задан */
  contact: z.string().nullable(),
  demoEnabled: z.boolean(),
  /** Название демо-компании — для кнопки «Попробовать демо» */
  demoCompany: z.string().nullable(),
  /** Можно ли зарегистрировать компанию самостоятельно (ГЛ-05) */
  signupEnabled: z.boolean().default(false),
});
export type SiteInfo = z.infer<typeof SiteInfo>;

/* ───────────────────────── Расчёт состояния ───────────────────────── */

/** Дней между датами по календарю (в UTC), без учёта времени суток */
export function daysBetween(fromIso: string, toIso: string): number {
  const a = Date.UTC(
    Number(fromIso.slice(0, 4)),
    Number(fromIso.slice(5, 7)) - 1,
    Number(fromIso.slice(8, 10)),
  );
  const b = Date.UTC(Number(toIso.slice(0, 4)), Number(toIso.slice(5, 7)) - 1, Number(toIso.slice(8, 10)));
  return Math.round((b - a) / 86_400_000);
}

/** Прибавить дни к дате ГГГГ-ММ-ДД */
export function addDays(dateIso: string, days: number): string {
  const t = Date.UTC(
    Number(dateIso.slice(0, 4)),
    Number(dateIso.slice(5, 7)) - 1,
    Number(dateIso.slice(8, 10)),
  );
  return new Date(t + days * 86_400_000).toISOString().slice(0, 10);
}

/** Состояние лицензии по датам: сегодня — «ГГГГ-ММ-ДД» по часам компании */
export function licenseStateOf(
  lic: { suspended: boolean; endsAt: string | null } | null,
  today: string,
): { state: LicenseState; daysLeft: number | null } {
  if (!lic) return { state: 'none', daysLeft: null };
  if (lic.suspended)
    return { state: 'suspended', daysLeft: lic.endsAt ? daysBetween(today, lic.endsAt) : null };
  if (!lic.endsAt) return { state: 'active', daysLeft: null };
  const daysLeft = daysBetween(today, lic.endsAt);
  if (daysLeft < -GRACE_DAYS) return { state: 'readonly', daysLeft };
  if (daysLeft < 0) return { state: 'grace', daysLeft };
  if (daysLeft <= EXPIRY_WARNINGS[0]) return { state: 'expiring', daysLeft };
  return { state: 'active', daysLeft };
}

export const READ_ONLY_STATES: LicenseState[] = ['readonly', 'suspended', 'none'];

/** Текст плашки о состоянии лицензии для сотрудников компании; null — плашка не нужна */
export function licenseBannerText(
  s: Pick<LicenseStatus, 'state' | 'daysLeft' | 'endsAt'> & {
    kind?: LicenseKind | null;
    autoRenew?: boolean;
  },
): string | null {
  const what = s.kind === 'trial' ? 'Пробный период' : 'Лицензия компании';
  switch (s.state) {
    case 'expiring':
      // Автопродление: продлевает владелец портала, компанию не тревожим
      if (s.autoRenew) return null;
      return s.daysLeft === 0
        ? `${what} заканчивается сегодня.`
        : `${what} заканчивается через ${s.daysLeft} дн.`;
    case 'grace': {
      const left = GRACE_DAYS + (s.daysLeft ?? 0);
      return left <= 0
        ? 'Срок лицензии вышел. Сегодня последний день льготного периода, завтра — только чтение.'
        : `Срок лицензии вышел. Ещё ${left} дн. портал работает полностью, затем — только чтение.`;
    }
    case 'readonly':
      return 'Срок лицензии истёк: данные доступны только для чтения, выгрузки работают, изменения закрыты.';
    case 'none':
      return 'У компании нет действующей лицензии: данные доступны только для чтения.';
    case 'suspended':
      return 'Доступ компании к порталу приостановлен владельцем портала.';
    default:
      return null;
  }
}
